import { PayoutDetails, PayoutForm } from "../../interfaces/OnboardingObject";

/**
 * Payout-details helpers for the UI: form defaults, validation and display.
 * Validation mirrors the user service (SettlementService.normalizePayout); the
 * server is the authority and re-validates. The wire body is built in
 * converters/uiToBackend/OnboardingConvertor (buildPayoutDetailsPayload).
 */

const UPI = /^[A-Za-z0-9._-]{2,256}@[A-Za-z][A-Za-z0-9.]{1,63}$/;
const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT = /^[0-9]{9,18}$/;
const PHONE = /^[6-9][0-9]{9}$/;

export const EMPTY_PAYOUT_FORM: PayoutForm = {
  method: "UPI",
  upiId: "",
  holder: "",
  account: "",
  ifsc: "",
  phone: "",
};

export type PayoutErrors = Partial<Record<keyof PayoutForm, string>>;

/** "+91 98765-43210" → "9876543210"; "" stays "". */
export const normalizePhone = (value: string): string => {
  let phone = value.trim().replace(/[\s-]/g, "");
  if (phone.startsWith("+91")) phone = phone.slice(3);
  return phone;
};

/** Field errors for the form; an empty object means valid. */
export function validatePayout(form: PayoutForm): PayoutErrors {
  const errors: PayoutErrors = {};
  const phone = normalizePhone(form.phone);
  if (phone && !PHONE.test(phone)) errors.phone = "Enter a 10-digit mobile number.";
  if (form.method === "UPI") {
    if (!UPI.test(form.upiId.trim())) errors.upiId = "Enter a UPI id like name@bank.";
  } else {
    const holder = form.holder.trim();
    if (!holder || holder.length > 120) errors.holder = "Enter the account holder's name.";
    if (!ACCOUNT.test(form.account.replace(/\s/g, ""))) errors.account = "Account number must be 9 to 18 digits.";
    if (!IFSC.test(form.ifsc.trim().toUpperCase())) errors.ifsc = "Enter an IFSC like ABCD0123456.";
  }
  return errors;
}

/** Saved details → form (prefill). */
export function toPayoutForm(payout: PayoutDetails | null | undefined): PayoutForm {
  if (!payout) return EMPTY_PAYOUT_FORM;
  return {
    method: payout.method,
    upiId: payout.upiId ?? "",
    holder: payout.holder ?? "",
    account: payout.account ?? "",
    ifsc: payout.ifsc ?? "",
    phone: payout.phone ?? "",
  };
}

/** Label/value rows for showing (and copying) payout details. */
export function payoutRows(payout: PayoutDetails): { label: string; value: string }[] {
  const rows =
    payout.method === "UPI"
      ? [{ label: "UPI id", value: payout.upiId ?? "" }]
      : [
          { label: "Account holder", value: payout.holder ?? "" },
          { label: "Account no.", value: payout.account ?? "" },
          { label: "IFSC", value: payout.ifsc ?? "" },
        ];
  if (payout.phone) rows.push({ label: "Phone", value: payout.phone });
  return rows.filter((row) => row.value);
}
